import React from "react";
import { Alert, Button, Spinner } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faArrowRight,
  faRotateRight,
  faTrashCan,
  faUpload,
  faXmark,
} from "@fortawesome/free-solid-svg-icons";

import {
  cancelArtworkOptimizationJob,
  markArtworkOptimizationUploadComplete,
  retryArtworkOptimizationUpload,
  uploadArtworkOptimizationSource,
} from "../../store/artworks/artworks.api";
import {
  PendingArtworkOptimizationJob,
  PendingArtworkOptimizationJobsResponse,
} from "../../types/artworkOptimization";
import workspaceTheme from "./workspaceTheme.module.css";

interface PendingOptimizationsWorkspaceTabProps {
  data: PendingArtworkOptimizationJobsResponse | null;
  loading: boolean;
  error: string | null;
  onRefresh: () => Promise<void>;
  onOpenJob: (jobId: string, mode: "review" | "restart") => void;
}

const NEEDS_ATTENTION = new Set<PendingArtworkOptimizationJob["status"]>([
  "awaiting_upload",
  "ready",
  "failed",
]);

const STATUS_LABELS: Record<PendingArtworkOptimizationJob["status"], string> = {
  awaiting_upload: "Awaiting upload",
  queued: "Queued",
  processing: "Processing",
  ready: "Ready",
  failed: "Failed",
};

const PROGRESS_STAGE_LABELS: Record<string, string> = {
  claimed: "Starting",
  downloading: "Downloading",
  validating: "Validating",
  analyzing_source: "Analyzing source",
  optimizing: "Optimizing",
  analyzing_output: "Analyzing output",
  writing_manifest: "Preparing result",
  complete: "Preparing upload",
  uploading: "Uploading result",
};

const formatFileSize = (bytes?: number): string => {
  if (typeof bytes !== "number" || !Number.isFinite(bytes) || bytes < 0) return "Unknown size";
  if (bytes < 1024) return `${bytes.toFixed(0)} B`;
  if (bytes < 1024 ** 2) return `${(bytes / 1024).toFixed(1)} KB`;
  if (bytes < 1024 ** 3) return `${(bytes / 1024 ** 2).toFixed(1)} MB`;
  return `${(bytes / 1024 ** 3).toFixed(1)} GB`;
};

const statusClass = (status: PendingArtworkOptimizationJob["status"]): string => {
  if (status === "ready") return workspaceTheme.statusSuccess;
  if (status === "failed") return workspaceTheme.statusError;
  if (status === "awaiting_upload") return workspaceTheme.statusWarning;
  return workspaceTheme.statusInfo;
};

const statusMessage = (job: PendingArtworkOptimizationJob): string => {
  if (job.status === "awaiting_upload") return "The source upload was interrupted. Select the same file to restart the upload.";
  if (job.status === "queued") return "Waiting for an optimization worker. You can leave this page.";
  if (job.status === "processing") return "Optimization is in progress. You can leave this page.";
  if (job.status === "ready") return "Optimization is ready for review.";
  return job.failure?.message || "The model could not be optimized.";
};

const isTakingLonger = (job: PendingArtworkOptimizationJob): boolean => {
  if (!job.createdAt || !["queued", "processing"].includes(job.status)) return false;
  const createdAt = new Date(job.createdAt).getTime();
  return Number.isFinite(createdAt) && Date.now() - createdAt > 15 * 60 * 1000;
};

const formatElapsed = (createdAt: string | null): string | null => {
  if (!createdAt) return null;
  const elapsedMinutes = Math.max(0, Math.floor((Date.now() - new Date(createdAt).getTime()) / 60000));
  if (!Number.isFinite(elapsedMinutes)) return null;
  if (elapsedMinutes < 1) return "Less than 1 min elapsed";
  if (elapsedMinutes < 60) return `${elapsedMinutes} min elapsed`;
  const hours = Math.floor(elapsedMinutes / 60);
  const minutes = elapsedMinutes % 60;
  return minutes ? `${hours} hr ${minutes} min elapsed` : `${hours} hr elapsed`;
};
export const PendingOptimizationsWorkspaceTab = ({
  data,
  loading,
  error,
  onRefresh,
  onOpenJob,
}: PendingOptimizationsWorkspaceTabProps) => {
  const [busyJobId, setBusyJobId] = React.useState<string | null>(null);
  const [actionError, setActionError] = React.useState<string | null>(null);
  const [uploadPercent, setUploadPercent] = React.useState(0);
  const [sourceUploadInProgress, setSourceUploadInProgress] = React.useState(false);
  const retryJobRef = React.useRef<PendingArtworkOptimizationJob | null>(null);
  const retryAbortRef = React.useRef<AbortController | null>(null);
  const retryCancellationInProgressRef = React.useRef(false);

  React.useEffect(() => {
    if (!sourceUploadInProgress) return undefined;
    const warnBeforeLeaving = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      event.returnValue = "";
    };
    window.addEventListener("beforeunload", warnBeforeLeaving);
    return () => window.removeEventListener("beforeunload", warnBeforeLeaving);
  }, [sourceUploadInProgress]);

  const checkUpload = async (job: PendingArtworkOptimizationJob) => {
    setBusyJobId(job.jobId);
    setActionError(null);
    try {
      await markArtworkOptimizationUploadComplete(job.jobId);
      await onRefresh();
    } catch (caught: any) {
      setActionError(
        caught?.response?.data?.error?.message || "The source upload could not be verified."
      );
    } finally {
      setBusyJobId(null);
    }
  };

  const chooseRetryFile = (job: PendingArtworkOptimizationJob) => {
    retryJobRef.current = job;
    const input = document.getElementById(`optimization-retry-${job.jobId}`) as HTMLInputElement | null;
    if (!input) return;
    input.value = "";
    input.click();
  };

  const retryUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const job = retryJobRef.current;
    const file = event.currentTarget.files?.[0];
    event.currentTarget.value = "";
    if (!job || !file || busyJobId !== null || retryAbortRef.current !== null) return;
    if (file.name !== job.fileName) {
      setActionError(`Select the original file named ${job.fileName}.`);
      return;
    }
    if (file.size !== job.fileSize) {
      setActionError(`The selected file size does not match the original ${formatFileSize(job.fileSize)} upload.`);
      return;
    }
    if (!file.name.toLowerCase().endsWith(".glb")) {
      setActionError("Select the original GLB file.");
      return;
    }

    const abortController = new AbortController();
    retryAbortRef.current = abortController;
    retryCancellationInProgressRef.current = false;
    setBusyJobId(job.jobId);
    setActionError(null);
    setUploadPercent(0);
    setSourceUploadInProgress(true);
    try {
      const retry = await retryArtworkOptimizationUpload(job.jobId, {
        fileName: file.name,
        fileSize: file.size,
        contentType: "model/gltf-binary",
      });
      await uploadArtworkOptimizationSource(retry.upload, file, {
        signal: abortController.signal,
        onProgress: setUploadPercent,
      });
      await markArtworkOptimizationUploadComplete(job.jobId);
      await onRefresh();
    } catch (caught: any) {
      if (!abortController.signal.aborted) {
        setActionError(caught?.response?.data?.error?.message || "The source upload could not be restarted.");
      }
    } finally {
      setSourceUploadInProgress(false);
      if (retryAbortRef.current === abortController) {
        retryAbortRef.current = null;
      }
      if (!retryCancellationInProgressRef.current) {
        setBusyJobId(null);
        retryJobRef.current = null;
      }
    }
  };

  const cancelActiveUpload = async (job: PendingArtworkOptimizationJob) => {
    if (!window.confirm("Cancel this source upload and optimization?")) return;

    retryCancellationInProgressRef.current = true;
    retryAbortRef.current?.abort();
    setBusyJobId(job.jobId);
    setActionError(null);
    try {
      await cancelArtworkOptimizationJob(job.jobId);
      await onRefresh();
    } catch (caught: any) {
      setActionError(caught?.response?.data?.error?.message || "The source upload could not be cancelled.");
    } finally {
      retryAbortRef.current = null;
      retryCancellationInProgressRef.current = false;
      retryJobRef.current = null;
      setSourceUploadInProgress(false);
      setBusyJobId(null);
    }
  };

  const closeJob = async (job: PendingArtworkOptimizationJob, dismiss: boolean) => {
    const confirmation = job.status === "processing"
      ? "Cancel this optimization? Publication will stop, but worker cleanup may take a short time."
      : dismiss
        ? "Dismiss this failed optimization?"
        : "Cancel this optimization?";
    if (!window.confirm(confirmation)) return;

    setBusyJobId(job.jobId);
    setActionError(null);
    try {
      await cancelArtworkOptimizationJob(job.jobId);
      await onRefresh();
    } catch (caught: any) {
      setActionError(caught?.response?.data?.error?.message || "The optimization could not be cancelled.");
    } finally {
      setBusyJobId(null);
    }
  };

  if (loading && !data) {
    return <div className="py-5 text-center"><Spinner animation="border" /></div>;
  }

  if (!data) {
    return (
      <Alert variant="danger" className={workspaceTheme.surfaceAlert}>
        <div className="d-flex align-items-center justify-content-between gap-3 flex-wrap">
          <span>{error || "Pending optimizations could not be loaded."}</span>
          <Button
            variant="light"
            className={`${workspaceTheme.workspaceButton} ${workspaceTheme.workspaceButtonSecondary} ${workspaceTheme.workspaceButtonSmall}`}
            onClick={() => void onRefresh()}
          >
            <FontAwesomeIcon icon={faRotateRight} aria-hidden="true" />
            Retry
          </Button>
        </div>
      </Alert>
    );
  }

  const needsAttention = data.jobs.filter((job) => NEEDS_ATTENTION.has(job.status));
  const inProgress = data.jobs.filter((job) => !NEEDS_ATTENTION.has(job.status));
  const actionInProgress = busyJobId !== null;

  const renderJob = (job: PendingArtworkOptimizationJob) => {
    const title = job.title || job.fileName || "Untitled artwork";
    const busy = busyJobId === job.jobId;
    const retryUploadActive = job.status === "awaiting_upload" && busy && sourceUploadInProgress;
    const elapsed = ["queued", "processing"].includes(job.status) ? formatElapsed(job.createdAt) : null;
    return (
      <article className={workspaceTheme.pendingOptimizationRow} key={job.jobId}>
        <div className={workspaceTheme.pendingOptimizationMain}>
          <div className="d-flex align-items-center gap-2 flex-wrap">
            <h4 className={workspaceTheme.pendingOptimizationTitle}>{title}</h4>
            <span className={`${workspaceTheme.statusChip} ${statusClass(job.status)}`}>
              {STATUS_LABELS[job.status]}
            </span>
          </div>
          <div className={workspaceTheme.pendingOptimizationMeta}>
            <span>{job.fileName || "GLB source"}</span>
            <span>{job.preset}</span>
            <span>{formatFileSize(job.fileSize)}</span>
            {elapsed ? <span>{elapsed}</span> : null}
          </div>
          <p className={`mb-0 ${workspaceTheme.neutralText}`}>{statusMessage(job)}</p>
          {retryUploadActive ? (
            <div className={workspaceTheme.pendingOptimizationProgress}>
              <div className={workspaceTheme.pendingOptimizationProgressHeader}>
                <span>Uploading source</span>
                <span>{uploadPercent}%</span>
              </div>
              <progress aria-label={`Source upload progress for ${title}`} max={100} value={uploadPercent} />
            </div>
          ) : null}
          {job.status === "processing" && job.progress ? (
            <div className={workspaceTheme.pendingOptimizationProgress}>
              <div className={workspaceTheme.pendingOptimizationProgressHeader}>
                <span>{PROGRESS_STAGE_LABELS[job.progress.stage] || "Processing"}</span>
                <span>{Math.round(job.progress.percent)}%</span>
              </div>
              <progress
                aria-label={`Optimization progress for ${title}`}
                max={100}
                value={job.progress.percent}
              />
            </div>
          ) : null}
          {isTakingLonger(job) ? (
            <p className={`mb-0 ${workspaceTheme.pendingOptimizationLongRunning}`}>This is taking longer than usual.</p>
          ) : null}
          {job.metrics?.sourceBytes !== undefined || job.metrics?.optimizedBytes !== undefined ? (
            <div className={workspaceTheme.pendingOptimizationMetrics}>
              {job.metrics?.sourceBytes !== undefined ? <span>Source {formatFileSize(job.metrics.sourceBytes)}</span> : null}
              {job.metrics?.optimizedBytes !== undefined ? <span>Optimized {formatFileSize(job.metrics.optimizedBytes)}</span> : null}
            </div>
          ) : null}
        </div>
        <div className={workspaceTheme.pendingOptimizationActions}>
          {job.status === "awaiting_upload" ? (
            <>
              <input
                id={`optimization-retry-${job.jobId}`}
                className="visually-hidden"
                type="file"
                accept=".glb,model/gltf-binary"
                disabled={actionInProgress}
                tabIndex={-1}
                aria-label={`Select source file for ${title}`}
                onChange={(event) => void retryUpload(event)}
              />
              <Button
                variant="light"
                disabled={actionInProgress}
                aria-label={`Retry upload for ${title}`}
                className={`${workspaceTheme.workspaceButton} ${workspaceTheme.workspaceButtonPrimary} ${workspaceTheme.workspaceButtonSmall}`}
                onClick={() => chooseRetryFile(job)}
              >
                <FontAwesomeIcon icon={faUpload} aria-hidden="true" />
                Retry upload
              </Button>
              <Button
                variant="light"
                disabled={actionInProgress}
                aria-label={`Check upload for ${title}`}
                className={`${workspaceTheme.workspaceButton} ${workspaceTheme.workspaceButtonSecondary} ${workspaceTheme.workspaceButtonSmall}`}
                onClick={() => void checkUpload(job)}
              >
                <FontAwesomeIcon icon={faRotateRight} aria-hidden="true" />
                Check upload
              </Button>
            </>
          ) : null}
          {job.status === "ready" ? (
            <Button
              variant="light"
              disabled={actionInProgress}
              aria-label={`Review ${title}`}
              className={`${workspaceTheme.workspaceButton} ${workspaceTheme.workspaceButtonPrimary} ${workspaceTheme.workspaceButtonSmall}`}
              onClick={() => onOpenJob(job.jobId, "review")}
            >
              Review
              <FontAwesomeIcon icon={faArrowRight} aria-hidden="true" />
            </Button>
          ) : null}
          {job.status === "failed" ? (
            <>
              <Button
                variant="light"
                disabled={actionInProgress}
                aria-label={`Start over ${title}`}
                className={`${workspaceTheme.workspaceButton} ${workspaceTheme.workspaceButtonSecondary} ${workspaceTheme.workspaceButtonSmall}`}
                onClick={() => onOpenJob(job.jobId, "restart")}
              >
                <FontAwesomeIcon icon={faRotateRight} aria-hidden="true" />
                Start over
              </Button>
              <Button
                variant="outline-light"
                disabled={actionInProgress}
                aria-label={`Dismiss ${title}`}
                className={`${workspaceTheme.workspaceButton} ${workspaceTheme.workspaceButtonSmall}`}
                onClick={() => void closeJob(job, true)}
              >
                <FontAwesomeIcon icon={faTrashCan} aria-hidden="true" />
                Dismiss
              </Button>
            </>
          ) : null}
          {["awaiting_upload", "queued", "processing"].includes(job.status) ? (
            <Button
              variant="outline-light"
              disabled={actionInProgress && !retryUploadActive}
              aria-label={`Cancel ${title}`}
              className={`${workspaceTheme.workspaceButton} ${workspaceTheme.workspaceButtonSmall}`}
              onClick={() => void (retryUploadActive ? cancelActiveUpload(job) : closeJob(job, false))}
            >
              <FontAwesomeIcon icon={faXmark} aria-hidden="true" />
              Cancel
            </Button>
          ) : null}
        </div>
      </article>
    );
  };

  return (
    <section className={workspaceTheme.pendingOptimizationPanel} aria-label="Pending optimizations">
      <div className={workspaceTheme.pendingOptimizationHeader}>
        <div>
          <h3 className="h5 mb-1">Pending Optimizations</h3>
          <div className={workspaceTheme.secondaryText}>{data.quota.activeCount} of {data.quota.maxActiveJobs} active slots</div>
        </div>
        <Button
          variant="light"
          disabled={loading || actionInProgress}
          aria-label="Refresh pending optimizations"
          className={`${workspaceTheme.workspaceButton} ${workspaceTheme.workspaceButtonSecondary} ${workspaceTheme.workspaceButtonSmall}`}
          onClick={() => void onRefresh()}
        >
          <FontAwesomeIcon icon={faRotateRight} aria-hidden="true" />
          Refresh
        </Button>
      </div>

      {error || actionError ? (
        <Alert variant="danger" className={workspaceTheme.surfaceAlert}>{actionError || error}</Alert>
      ) : null}

      {data.jobs.length === 0 ? (
        <div className={workspaceTheme.emptyState}>No pending optimizations.</div>
      ) : (
        <div className="d-flex flex-column gap-4">
          {needsAttention.length > 0 ? (
            <div>
              <h4 className={workspaceTheme.pendingOptimizationGroupTitle}>Needs attention</h4>
              <div className={workspaceTheme.pendingOptimizationList}>{needsAttention.map(renderJob)}</div>
            </div>
          ) : null}
          {inProgress.length > 0 ? (
            <div>
              <h4 className={workspaceTheme.pendingOptimizationGroupTitle}>In progress</h4>
              <div className={workspaceTheme.pendingOptimizationList}>{inProgress.map(renderJob)}</div>
            </div>
          ) : null}
        </div>
      )}

      {data.hasMore ? (
        <p className={`mb-0 ${workspaceTheme.secondaryText}`}>Showing the first 20 jobs that need attention.</p>
      ) : null}
    </section>
  );
};
